<template>
  <!-- required for 404 handling -->
  <div v-if="errorCaused"></div>
  <div v-else-if="fetching || !story" class="mx-5 space-y-2 py-4">
    <div class="max-w-xl">
      <Skeleton class="mb-10" full-width />
    </div>
    <Skeleton type="custom" class="h-6 w-72" />
    <Skeleton type="custom" class="h-6 w-48" />

    <Skeleton type="custom" class="mb-10 h-6 w-72" />
    <Skeleton type="custom" class="h-40" />
  </div>
  <div v-else v-show="isStoryReleased">
    <div v-if="'teaser_image' in story.content">
      <CmsImage :blok="story.content as any" is-teaser />
    </div>
    <div class="mx-5">
      <div class="py-4">
        <Breadcrumbs
          :links="[{ value: translatedSlugName, to: story.full_slug }]"
        />
      </div>

      <template v-if="preContent && !isCancellationForm">
        <component
          :is="content.component"
          v-for="content in preContent"
          :key="content._uid"
          :blok="content"
          class="mb-4.5"
        />
      </template>

      <Story :story="story" />

      <template v-if="postContent && !isCancellationForm">
        <component
          :is="content.component"
          v-for="content in postContent"
          :key="content._uid"
          :blok="content"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { provide } from 'vue'
import type { SbContentPage } from '~/cms/storyblok/types/storyblok.d.ts'
import { useI18n } from '#i18n'
import useCms from '~/composables/cms/useCms'
import { STORY_KEY } from '#layers/base/constants/injectionKeys'

useRouteCache((helper) => helper.setMaxAge(3600).setCacheable())

const i18n = useI18n()
const { toLocalePath } = useRouteHelpers()
const errorCaused = ref(false)
const { params } = useRoute()
const { code: currentLocaleCode } = i18n.localeProperties.value

const { data, fetching, fetchBySlug, metaLinks, excluded } =
  useCms<SbContentPage>(`cms-content-page-${params.slug}`)
const story = computed(() => data.value)
provide(STORY_KEY, story)

const { name: translatedSlugName } = useTranslatedSlug(story)

const isStoryReleased = computed(() =>
  story.value?.content?.schedule?.length
    ? +new Date() > +new Date(story.value?.content?.schedule)
    : true,
)

const scheduleFallbackUrl = computed(
  () => story.value?.content?.scheduleFallbackUrl,
)

const metaDataExtensions = computed(() => ({
  link: metaLinks.value,
}))

useStoryMetaData({ data: story, fetching, extensions: metaDataExtensions })

// Only redirects. Visibility is bound straight to `isStoryReleased`, which both
// server and client derive from the same payload — a separate flag flipped from
// here rendered the story hidden until this ran, blanking the page mid-hydration
// and costing the browser its scroll anchor on reload (ECOM-2794).
const { redirectIfNeeded } = useStoryRedirect()

// ECOM-2385: a ContentPage with an active `redirectUrl` forwards the visitor to
// the target (SSR 302, no new history entry). Returns true when it redirected,
// so the caller skips the 404 path, schedule guard, and render.
const redirectGuard = () =>
  redirectIfNeeded((story.value?.content as any)?.redirectUrl)
const scheduleGuard = () => {
  if (!story.value) {
    return
  }
  if (!fetching.value && !isStoryReleased.value) {
    if (scheduleFallbackUrl.value?.cached_url?.length) {
      if (scheduleFallbackUrl.value.linktype === 'story') {
        navigateTo(
          {
            path: `/${currentLocaleCode}/${scheduleFallbackUrl.value.cached_url}`,
          },
          { replace: true },
        )
      } else if (import.meta.client) {
        window.location.href = scheduleFallbackUrl.value.cached_url
      }
    } else {
      navigateTo(toLocalePath({ name: 'index' }), { replace: true })
    }
  }
}

await fetchOnNavigation(async () => {
  await fetchBySlug(
    `c/${Array.isArray(params.slug) ? params.slug.join('/') : params.slug}`,
  )

  if (await redirectGuard()) {
    return
  }

  if (excluded.value || !story.value) {
    errorCaused.value = true
    throw createError({
      statusCode: 404,
      message: `Not found`,
    })
  } else {
    scheduleGuard()
  }
})

onMounted(async () => {
  if (await redirectGuard()) {
    return
  }
  scheduleGuard()
})

const preContent = computed(
  () => (story.value?.content as any)?.preContent || [],
)
const postContent = computed(
  () => (story.value?.content as any)?.postContent || [],
)

// The OrderCancellationRequestForm story owns the rendering of its own
// pre-/postContent (so it can hide them alongside the form on success). Skip
// the generic page-level rendering for it to avoid duplicating that content.
const isCancellationForm = computed(
  () =>
    (story.value?.content as any)?.component === 'OrderCancellationRequestForm',
)

defineOptions({ name: 'ContentPage' })
definePageMeta({ pageType: 'static' })
</script>
